import classNames from 'classnames';
import React from 'react';
import * as Splashes from '@/assets/graphics/splashes/splashes';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { capitalizeFirstLetter } from '@/libraries/formatters/capitalize-first-letter';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './splash.module.scss';

export interface SplashProps {
  /** The class name to apply */
  className?: string;
  /** The highlighted text to display */
  highlightedText?: Content.Components['SplashDto']['highlightedText'];
  /** The image to replace the whole splash */
  image?: Content.Components['SplashDto']['image'];
  /** The name of the splash to show */
  splashTheme?: Splashes.Theme;
  /** The type of splash to show */
  splashType?: Content.Components['SplashDto']['splashType'];
  /** Text to display inside the splash */
  text?: Content.Components['SplashDto']['text'];
}

const Splash = ({
  className,
  highlightedText,
  image,
  splashTheme = 'primary',
  splashType = 'normal',
  text,
}: SplashProps) => {
  const { brand } = usePageDetailsContext();
  const isLaptop = useWindowSize('laptop');

  const isImageSplash =
    image && (splashType === 'image' || splashType === 'highlightedImage');

  const splashSize = isImageSplash
    ? isLaptop && splashType === 'highlightedImage'
      ? 'Large'
      : isLaptop
        ? 'Medium'
        : 'Small'
    : isLaptop && splashType === 'highlighted'
      ? highlightedText
        ? 'Large'
        : 'Medium'
      : 'Small';

  const name = `${capitalizeFirstLetter(`${brand}`)}${splashSize}`;

  const SplashComponent = Splashes[name];

  if (!SplashComponent) return null;

  if (splashType === 'highlighted' && !text && !highlightedText) return null;

  if (splashType === 'normal' && !text) return null;

  if (splashType === 'image' && !image) return null;

  if (splashType === 'highlightedImage' && !image) return null;

  const renderContent = () => {
    if (isImageSplash) {
      return (
        <Image
          alt={image.altText}
          focalPoint={image.focalPoint}
          height={240}
          priority
          src={image.url}
          title={image.name}
          unoptimized
          width={240}
        />
      );
    }

    return (
      <React.Fragment>
        <SplashComponent className={styles.splash} />

        <div className={styles.textWrapper}>
          {text && (
            <Typography className={styles.text} tagStyle="labelMedium">
              {text}
            </Typography>
          )}

          {highlightedText && (
            <Typography className={styles.text} tagStyle="splashOffer">
              {highlightedText}
            </Typography>
          )}
        </div>
      </React.Fragment>
    );
  };

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.isLarge]: splashSize === 'Large',
        [styles.isMedium]: splashSize === 'Medium',
        [styles.isSmall]: splashSize === 'Small',
        [styles.isPrimary]: splashTheme === 'primary',
        [styles.isSecondary]: splashTheme === 'secondary',
        [styles.isTertiary]: splashTheme === 'tertiary',
        [styles.isDefault]: brand === 'default',
        [styles.isGolf]: brand === 'golf',
        [styles.isRejseklubben]: brand === 'rejseklubben',
        [styles.isSafari]: brand === 'safari',
      })}
    >
      {renderContent()}
    </div>
  );
};

Splash.displayName = 'Splash';

export default Splash;
